import React from 'react';

import { ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';

import Flexicon from '../shared/Flexicon';

export const LAYOUT_HEADER_ROW_CLASS_NAME = 'flex min-w-0 shrink-0 items-center gap-3 px-4 pt-4 pb-3';

export const LayoutHeaderRow = React.memo(function LayoutHeaderRow({ children, className, ...props }) {
  return (
    <div className={cn(LAYOUT_HEADER_ROW_CLASS_NAME, className)} {...props}>
      {children}
    </div>
  );
});

export const LayoutUnreadDot = React.memo(function LayoutUnreadDot({ className }) {
  return <span className={cn('absolute -top-1 -right-1 size-2 rounded-full bg-blue-500', className)} aria-hidden />;
});

export const LayoutHeaderIconButton = React.memo(function LayoutHeaderIconButton({
  className,
  children,
  onImage = false,
  surfaceless = false,
  ...props
}) {
  return (
    <button
      type="button"
      data-app-region="none"
      className={cn(
        'relative flex size-9 shrink-0 transform-gpu cursor-pointer items-center justify-center rounded-full p-1.5 transition-transform duration-150 active:scale-[0.96]',
        // The light surface must not render alongside the glass one: its shadow class
        // fights bg-image-glass-card's box-shadow (inset hairline + drop shadow) and
        // whichever lands later in the stylesheet wins, flattening the glass buttons.
        !onImage && 'border-[0.5px] border-white bg-gray-50 shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] dark:border-white/10',
        // Surfaceless buttons (inside the nav pill) must not carry a backdrop-filter of
        // their own — even a no-op one punches a hole in the pill's glass in Chromium —
        // so the on-image surface is skipped entirely rather than applied and cancelled.
        onImage && 'text-white dark:text-white',
        onImage && !surfaceless && 'bg-image-glass-card hover:bg-black/40 dark:hover:bg-black/40',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

export const LayoutHeaderNavButton = React.memo(function LayoutHeaderNavButton({
  onClick,
  active,
  title,
  children,
  className,
  activeClassName = 'bg-white text-black dark:bg-neutral-700 dark:text-white',
  showUnreadDot = false,
  unreadDotClassName,
  onImage = false,
  ...buttonProps
}) {
  return (
    <LayoutHeaderIconButton
      onClick={onClick}
      title={title}
      aria-label={title}
      onImage={onImage}
      {...buttonProps}
      className={cn(
        onImage ? 'text-white' : active ? activeClassName : 'text-gray-500 hover:bg-white dark:text-neutral-400 dark:hover:bg-neutral-800',
        className,
      )}
    >
      {children}
      {showUnreadDot ? <LayoutUnreadDot className={unreadDotClassName} /> : null}
    </LayoutHeaderIconButton>
  );
});

export const LayoutWorkspaceHeaderButton = React.memo(function LayoutWorkspaceHeaderButton({
  onClick,
  active,
  title,
  resource,
  onImage = false,
  children,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      data-app-region="none"
      className={cn(
        'flex h-9 max-w-[min(52vw,360px)] min-w-0 cursor-pointer items-center justify-self-center rounded-full border-[0.5px] border-white bg-gray-50 py-1 pr-3 pl-[3.5px] text-black shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] backdrop-blur-md hover:bg-white dark:border-white/10 dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-800',
        active && !onImage && 'bg-white dark:bg-neutral-800',
        onImage &&
          'border-0 bg-black/60 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.2),0_4px_24px_0_rgba(0,0,0,0.5)] backdrop-blur-[15px] backdrop-saturate-200 hover:bg-black/40 dark:text-white dark:hover:bg-black/40',
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <Flexicon
          resource={resource}
          size="size-7"
          rounded="rounded"
          className="size-7 shrink-0 border-[0.5px] border-black/10 dark:border-white/10"
        />
        <span className="flex min-w-0 items-center gap-1">
          <span className="min-w-0 truncate text-sm leading-[22px] font-medium transition-none">{children}</span>
          <ChevronRight
            className={cn('size-3.5 shrink-0 transition-none', onImage ? 'text-white' : 'text-gray-400')}
            strokeWidth={2}
            absoluteStrokeWidth
          />
        </span>
      </span>
    </button>
  );
});
